import React, { useState } from "react";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import { changePasswordAPI } from "../../../api/index";
import "./FindPasswordModal.css";

const FindPasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    username: "",
    name: "",
    phone: "",
    email: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const resetForm = () => {
    setFormData({
      username: "",
      name: "",
      phone: "",
      email: "",
      newPassword: "",
      confirmPassword: "",
    });
    setErrors({});
    setIsLoading(false);
    setIsSubmitted(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, "");

    // 길이에 따라 하이픈 추가
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(
        7,
        11
      )}`;
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // 휴대폰번호 필드인 경우 자동 포맷팅
    if (field === "phone") {
      processedValue = formatPhoneNumber(value);
    } else {
      // 다른 필드는 그대로 저장
      processedValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleBlur = (field) => {
    // 휴대폰번호는 포맷팅을 유지하므로 trim하지 않음
    if (field === "phone") {
      return;
    }

    // 포커스가 벗어날 때 trim 적용
    const trimmedValue = formData[field].trim();

    setFormData((prev) => ({
      ...prev,
      [field]: trimmedValue,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    const trimmedData = {
      username: formData.username.trim(),
      name: formData.name.trim(),
      phone: formData.phone.replace(/[^0-9]/g, ""), // 하이픈 제거
      email: formData.email.trim(),
      newPassword: formData.newPassword,
      confirmPassword: formData.confirmPassword,
    };

    if (!trimmedData.username) {
      newErrors.username = "아이디를 입력해주세요";
    }

    if (!trimmedData.name) {
      newErrors.name = "이름을 입력해주세요";
    }

    if (!trimmedData.phone) {
      newErrors.phone = "휴대폰번호를 입력해주세요";
    } else if (trimmedData.phone.length < 10) {
      newErrors.phone = "올바른 휴대폰번호를 입력해주세요";
    } else if (!/^01[0-9]/.test(trimmedData.phone)) {
      newErrors.phone = "010, 011, 012, 019로 시작하는 번호를 입력해주세요";
    }

    if (!trimmedData.email) {
      newErrors.email = "이메일을 입력해주세요";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedData.email)) {
      newErrors.email = "올바른 이메일 형식을 입력해주세요";
    }

    if (!trimmedData.newPassword) {
      newErrors.newPassword = "새 비밀번호를 입력해주세요";
    } else if (trimmedData.newPassword.length < 6) {
      newErrors.newPassword = "비밀번호는 6자 이상이어야 합니다";
    }

    if (trimmedData.newPassword !== trimmedData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호를 다시 입력해주세요";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);

      try {
        const apiData = {
          username: formData.username.trim(),
          name: formData.name.trim(),
          phone: formData.phone.replace(/[^0-9]/g, ""), // 하이픈 제거
          new_password: formData.newPassword,
        };

        const result = await changePasswordAPI(apiData);

        if (result === "" || result === "비밀번호가 변경되었습니다.") {
          setIsSubmitted(true);
        } else {
          alert(`비밀번호 변경 실패: ${result}`);
        }
      } catch (error) {
        console.error("비밀번호 변경 오류:", error);
        alert("비밀번호 변경 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    } else {
      console.error("폼 검증 실패:", errors);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="find-password-modal-overlay">
      <div className="find-password-modal" onClick={(e) => e.stopPropagation()}>
        <div className="find-password-modal-header">
          <h2>비밀번호 찾기</h2>
          <button className="close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        {!isSubmitted ? (
          <form className="find-password-form" onSubmit={handleSubmit}>
            <div className="form-description">
              가입 시 등록한 정보를 입력하고 새로운 비밀번호를 설정해주세요.
            </div>

            <div className="form-fields">
              <div className="form-field">
                <div className="form-label">아이디</div>
                <div className="form-input-item">
                  <Input
                    type="text"
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                    onBlur={() => handleBlur("username")}
                    placeholder="아이디를 입력해주세요"
                    error={errors.username}
                  />
                </div>
              </div>

              <div className="form-field">
                <div className="form-label">이름</div>
                <div className="form-input-item">
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    onBlur={() => handleBlur("name")}
                    placeholder="이름을 입력해주세요"
                    error={errors.name}
                  />
                </div>
              </div>

              <div className="form-field">
                <div className="form-label">휴대폰번호</div>
                <div className="form-input-item">
                  <Input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="010-1234-5678"
                    error={errors.phone}
                    maxLength={13}
                  />
                </div>
              </div>

              <div className="form-field">
                <div className="form-label">이메일</div>
                <div className="form-input-item">
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onBlur={() => handleBlur("email")}
                    placeholder="이메일을 입력해주세요"
                    error={errors.email}
                  />
                </div>
              </div>

              <div className="form-field">
                <div className="form-label">새 비밀번호</div>
                <div className="form-input-item">
                  <Input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) =>
                      handleInputChange("newPassword", e.target.value)
                    }
                    placeholder="새 비밀번호를 입력해주세요"
                    error={errors.newPassword}
                  />
                </div>
              </div>

              <div className="form-field">
                <div className="form-label">새 비밀번호 확인</div>
                <div className="form-input-item">
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    placeholder="새 비밀번호를 다시 입력해주세요"
                    error={errors.confirmPassword}
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                size="large"
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? "변경 중..." : "변경하기"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="large"
                onClick={handleClose}
              >
                취소
              </Button>
            </div>
          </form>
        ) : (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>비밀번호가 변경되었습니다</h3>
            <p>
              비밀번호가 성공적으로 변경되었습니다.
              <br />
              새로운 비밀번호로 로그인해주세요.
            </p>
            <Button
              type="button"
              variant="primary"
              size="large"
              onClick={handleClose}
              className="close-success-btn"
            >
              확인
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindPasswordModal;
